import classNames from 'classnames'
import {
  motion,
  useAnimate,
  type AnimationPlaybackControlsWithThen,
  type AnimationSequence,
} from 'framer-motion'
import { useCallback, useEffect, useRef } from 'react'
import Marquee from 'react-fast-marquee'
import { useShallow } from 'zustand/react/shallow'
import { formatDecimalPlaces } from '../../../../../../helpers/functions/format-decimal-places.ts'
import { useGlobalStore } from '../../../../../../store/global-store.ts'
import type { Gift } from '../../../../../../types/lives/messages.ts'
import appLogo from '../assets/app-logo.png'
import coin from '../assets/coin.png'
import { useGetGameEnabledFirst } from '../../../../../../hooks/queries/games/games-enableds/use-get-game-enabled-first.ts'

interface Props {
  gift: Gift
  isLocked: boolean
}

export function Gift({ gift, isLocked = false }: Props) {
  const [scope, animate] = useAnimate()

  const animationRef = useRef<AnimationPlaybackControlsWithThen>(null)

  const { isSelected } = useGlobalStore(
    useShallow((state) => ({
      isSelected:
        state.giftSelected?.id === gift?.id ||
        state.modalGiftOpen.giftId === gift?.id,
    })),
  )

  const { gameEnabled } = useGetGameEnabledFirst()

  const onSelectGift = useCallback(
    (gift: Gift) => {
      if (gift?.isGiftBox && !gameEnabled?.enableGameLuckGiftBox) {
        return
      }

      useGlobalStore.setState({ isSendQuick: false, giftSelected: gift })
    },
    [gameEnabled?.enableGameLuckGiftBox],
  )

  const changeQuantity = useCallback((quantity: number) => {
    useGlobalStore.setState({ quantity })
  }, [])

  const handleSelect = useCallback(() => {
    if (isLocked) {
      return
    }

    onSelectGift(gift)
    changeQuantity(1)
  }, [isLocked, gift, onSelectGift, changeQuantity])

  useEffect(() => {
    const sequence: AnimationSequence = [
      [scope.current, { scale: 1.2 }, { duration: 1 }],
      [scope.current, { scale: 1 }, { duration: 1 }],
    ]

    if (isSelected) {
      animationRef.current = animate(sequence, {
        repeat: Infinity,
        repeatType: 'mirror',
      })
    }

    if (!isSelected && animationRef.current) {
      animationRef.current.cancel()
    }
  }, [isSelected, scope, animate])

  if (gift?.isGiftBox && !gameEnabled?.enableGameLuckGiftBox) {
    return null
  }

  return (
    <div
      onClick={handleSelect}
      className={classNames([
        'h-25 flex flex-col items-center justify-center  text-white',
      ])}
    >
      <div
        className={classNames(
          'flex flex-col items-center justify-center rounded-sm text-white relative py-1 px-3 border border-transparent',
          {
            'bg-gray-700': isSelected,
          },
        )}
      >
        {isLocked && <img src={appLogo} className="w-10 absolute" />}

        <motion.div
          ref={scope}
          className={classNames({
            'opacity-50': isLocked,
          })}
        >
          <img src={gift.imgGift} className="w-8" />
        </motion.div>

        <div
          className={classNames('flex flex-col items-center mt-2 text-white', {
            'opacity-50': isLocked,
          })}
        >
          <div className="flex flex-col items-center justify-center flex-1">
            <span className="text-black text-center text-sm text-white">
              <Marquee
                direction="left"
                className="text-black text-center text-sm text-white"
                delay={1}
                speed={10}
                style={{ maxWidth: 65 }}
                pauseOnClick={true}
              >
                &nbsp;{gift.name}&nbsp;-
              </Marquee>
            </span>
          </div>

          <div className="flex flex-row items-center justify-center gap-1">
            {gift?.quantity && (
              <span className="text-black text-[10px] text-white">
                {formatDecimalPlaces(gift.quantity)}x
              </span>
            )}

            <img src={coin} className="w-2 h-2" />

            <span className="text-black text-[10px]  text-white">
              {formatDecimalPlaces(gift.value)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
